/**
 * Leitura de códigos de pagamento (boleto e Pix) vindos da câmera ou digitados.
 *
 * A câmera lê o CÓDIGO DE BARRAS do boleto (44 dígitos, formato ITF), mas o
 * provedor recebe a LINHA DIGITÁVEL (47 dígitos no boleto bancário, 48 na conta
 * de consumo/arrecadação). A conversão confere todos os dígitos verificadores:
 * leitura de ITF pela câmera às vezes devolve um código truncado ou com um
 * dígito trocado, e isso precisa ser descartado aqui, não no provedor.
 */

const onlyDigits = (value: string): string => value.replace(/\D/g, '')

/** Módulo 10 (pesos 2,1 da direita para a esquerda, somando os algarismos do produto). */
function mod10(digits: string): number {
    let sum = 0
    let weight = 2
    for (let i = digits.length - 1; i >= 0; i--) {
        let product = Number(digits[i]) * weight
        if (product > 9) product = Math.floor(product / 10) + (product % 10)
        sum += product
        weight = weight === 2 ? 1 : 2
    }
    return (10 - (sum % 10)) % 10
}

/** Soma do módulo 11 com pesos 2..9 da direita para a esquerda. */
function mod11Sum(digits: string): number {
    let sum = 0
    let weight = 2
    for (let i = digits.length - 1; i >= 0; i--) {
        sum += Number(digits[i]) * weight
        weight = weight === 9 ? 2 : weight + 1
    }
    return sum
}

/** DV geral do boleto bancário: resultado 0, 10 ou 11 vira 1. */
function bankBarcodeDv(digits43: string): number {
    const dv = 11 - (mod11Sum(digits43) % 11)
    return dv === 0 || dv === 10 || dv === 11 ? 1 : dv
}

/** DV módulo 11 da arrecadação: resto 0 ou 1 vira 0. */
function collectionMod11(digits: string): number {
    const rest = mod11Sum(digits) % 11
    return rest === 0 || rest === 1 ? 0 : 11 - rest
}

const isCollection = (barcode: string): boolean => barcode[0] === '8'

/** Arrecadação: o 3º dígito diz qual módulo calcula os DVs (6/7 = 10, 8/9 = 11). */
function collectionDvFn(barcode: string): ((digits: string) => number) | null {
    const id = barcode[2]
    if (id === '6' || id === '7') return mod10
    if (id === '8' || id === '9') return collectionMod11
    return null
}

function isValidBarcode(barcode: string): boolean {
    if (!/^\d{44}$/.test(barcode)) return false
    const withoutDv = (dvIndex: number) => barcode.slice(0, dvIndex) + barcode.slice(dvIndex + 1)
    if (isCollection(barcode)) {
        const dvFn = collectionDvFn(barcode)
        if (!dvFn) return false
        return dvFn(withoutDv(3)) === Number(barcode[3])
    }
    return bankBarcodeDv(withoutDv(4)) === Number(barcode[4])
}

/** Converte o código de barras (44 dígitos) na linha digitável. Devolve null se o código for inválido. */
export function barcodeToDigitableLine(raw: string): string | null {
    const barcode = onlyDigits(raw)
    if (!isValidBarcode(barcode)) return null

    if (isCollection(barcode)) {
        const dvFn = collectionDvFn(barcode)!
        return [0, 11, 22, 33]
            .map(start => {
                const block = barcode.slice(start, start + 11)
                return block + dvFn(block)
            })
            .join('')
    }

    const free = barcode.slice(19)
    const field1 = barcode.slice(0, 4) + free.slice(0, 5)
    const field2 = free.slice(5, 15)
    const field3 = free.slice(15, 25)
    return (
        field1 + mod10(field1) +
        field2 + mod10(field2) +
        field3 + mod10(field3) +
        barcode[4] +
        barcode.slice(5, 19)
    )
}

/** Converte a linha digitável (47 ou 48 dígitos) no código de barras. Devolve null se algum DV não conferir. */
export function digitableLineToBarcode(raw: string): string | null {
    const line = onlyDigits(raw)

    if (line.length === 48) {
        const blocks = [0, 12, 24, 36].map(start => line.slice(start, start + 12))
        const barcode = blocks.map(block => block.slice(0, 11)).join('')
        const dvFn = collectionDvFn(barcode)
        if (!dvFn || !isCollection(barcode)) return null
        if (blocks.some(block => dvFn(block.slice(0, 11)) !== Number(block[11]))) return null
        return isValidBarcode(barcode) ? barcode : null
    }

    if (line.length === 47) {
        const field1 = line.slice(0, 9)
        const field2 = line.slice(10, 20)
        const field3 = line.slice(21, 31)
        if (
            mod10(field1) !== Number(line[9]) ||
            mod10(field2) !== Number(line[20]) ||
            mod10(field3) !== Number(line[31])
        ) {
            return null
        }
        const barcode = line.slice(0, 4) + line[32] + line.slice(33, 47) + field1.slice(4) + field2 + field3
        return isValidBarcode(barcode) ? barcode : null
    }

    return null
}

export const isValidDigitableLine = (raw: string): boolean => digitableLineToBarcode(raw) !== null

/** Pix copia e cola (BR Code / EMV): começa pelo indicador de formato e traz o GUI do Pix. */
export function isPixPayload(raw: string): boolean {
    const value = raw.trim()
    return value.startsWith('000201') && value.toLowerCase().includes('br.gov.bcb.pix')
}

export type ScannedPaymentCode =
    | { kind: 'pix'; payload: string }
    | { kind: 'boleto'; digitableLine: string }
    | { kind: 'unknown' }

/**
 * Identifica o que foi lido: Pix copia e cola, código de barras de boleto
 * (44 dígitos) ou linha digitável já pronta (47/48 dígitos).
 */
export function classifyPaymentCode(raw: string): ScannedPaymentCode {
    const value = raw.trim()
    if (isPixPayload(value)) return { kind: 'pix', payload: value }

    const digits = onlyDigits(value)
    // Texto com letras não é boleto, mesmo que tenha 44 dígitos no meio.
    if (/[a-z]/i.test(value)) return { kind: 'unknown' }

    if (digits.length === 44) {
        const line = barcodeToDigitableLine(digits)
        return line ? { kind: 'boleto', digitableLine: line } : { kind: 'unknown' }
    }
    if ((digits.length === 47 || digits.length === 48) && isValidDigitableLine(digits)) {
        return { kind: 'boleto', digitableLine: digits }
    }
    return { kind: 'unknown' }
}

/** Formata a linha digitável em blocos, como vem impressa no boleto. */
export function formatDigitableLine(raw: string): string {
    const line = onlyDigits(raw)
    if (line.length === 47) {
        return `${line.slice(0, 5)}.${line.slice(5, 10)} ${line.slice(10, 15)}.${line.slice(15, 21)} ${line.slice(21, 26)}.${line.slice(26, 32)} ${line[32]} ${line.slice(33)}`
    }
    if (line.length === 48) {
        return [0, 12, 24, 36].map(start => `${line.slice(start, start + 11)}-${line[start + 11]}`).join(' ')
    }
    return raw
}
